import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useSessionValidation } from '../../../utils/sessionValidation';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Typography, Grid, Box, Snackbar, Alert,
  Chip, TextField, MenuItem, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon, Refresh, Add, Search,
  Warning, CheckCircle, Assignment
} from '@mui/icons-material';
import SaleReturnModal from './SaleReturnModal';
import LogoLoader from '../../common/LogoLoader';
import Pagination from '../../common/Pagination';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import MampillyLogo from '../Logo/logo_Madavana.png';

const SaleReturnIndex = () => {
  useSessionValidation();
  const { token, companyId, branchId, userId, permissions } = useAuth();
  const { logAudit } = useAuditLog();

  const hasCreatePermission = permissions?.includes('Create Master');
  const hasEditPermission = permissions?.includes('Edit Master');
  const hasDeletePermission = permissions?.includes('Delete Master');

  const [saleReturns, setSaleReturns] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [returnDetails, setReturnDetails] = useState([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editReturnData, setEditReturnData] = useState(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemInfo, setDeleteItemInfo] = useState({ billNumber: '', customer: '' });

  const [activateConfirmOpen, setActivateConfirmOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemInfo, setActivateItemInfo] = useState({ billNumber: '', customer: '' });

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const isMobile = useMediaQuery('(max-width:768px)');
  const [showSearch, setShowSearch] = useState(false);

  const formatDateToYYYYMMDD = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  
  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  
  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(formatDateToYYYYMMDD(new Date()));
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchCustomers = async () => {
    try {
      const res = await fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token);
      setCustomers(res.response_data || res || []);
    } catch (e) { console.error(e); }
  };

  const fetchSaleReturns = async () => {
    setLoading(true);
    setLoadingMessage('Fetching sale returns...');
    try {
      const activeRes = await fetchData('SaleReturnApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeRes.response_data || activeRes || []).map(i => ({ ...i, IsActive: i.IsActive ?? true }));
      let inactiveData = [];
      try {
        const inactiveRes = await fetchData('SaleReturnApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveRes.response_data || inactiveRes || []).map(i => ({ ...i, IsActive: false }));
      } catch (e) { console.error(e); }
      setSaleReturns([...activeData, ...inactiveData]);
    } catch (e) {
      console.error(e);
      setError('Failed to fetch sale returns');
      setSaleReturns([]);
    }
    setLoading(false);
  };

  const [initialBillData, setInitialBillData] = useState({ billNumber: '', billPrefix: '', finYear: '' });

  const handleOpenCreate = async () => {
    let billData = { billNumber: '1', billPrefix: '', finYear: '' };
    try {
      // Compute FinYear (Apr-Mar)
      const now = new Date();
      const yr = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
      const fy = `${String(yr).slice(-2)}-${String(yr + 1).slice(-2)}`;

      // Get prefix from branch
      let prefix = '';
      try {
        const branchRes = await fetchData('BranchApi', `/${companyId}`, token);
        const branchList = branchRes.response_data || branchRes || [];
        const arr = Array.isArray(branchList) ? branchList : [branchList];
        const branch = arr.find(b => String(b.Id) === String(branchId) || String(b.BranchId) === String(branchId)) || arr[0] || {};
        prefix = branch.SaleReturnPrefix || branch.saleReturnPrefix || 'SR';
      } catch (e) { prefix = 'SR'; }

      // Get max bill number from existing returns
      const from = `${yr}-04-01`;
      const to = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
      let nextNum = 1;
      try {
        const listRes = await fetchData('SaleReturnApi', `/ActiveList/${companyId}/${branchId}/${from}/${to}`, token);
        const list = listRes.response_data || listRes || [];
        const arr = Array.isArray(list) ? list : [];
        const maxNum = arr.reduce((m, r) => Math.max(m, parseInt(r.BillNumber || 0) || 0), 0);
        nextNum = maxNum + 1;
      } catch (e) { nextNum = 1; }

      billData = { billNumber: String(nextNum), billPrefix: prefix, finYear: fy };
    } catch (e) {
      console.error('Bill data error:', e);
    }
    setInitialBillData(billData);
    setCreateModalOpen(true);
  };

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchCustomers();
      fetchSaleReturns();
    }
  }, [companyId, token, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleView = async (sr) => {
    setLoading(true);
    setLoadingMessage('Loading details...');
    try {
      const res = await fetchData('SaleReturnApi', `/${sr.Id}`, token);
      const data = res.response_data || res;
      setSelectedReturn(data.SaleReturn || data);
      setReturnDetails(data.SaleReturnDetails || []);
      setViewModalOpen(true);
    } catch (e) {
      console.error(e);
      setError('Failed to fetch sale return details');
    }
    setLoading(false);
  };

  const handleEdit = async (sr) => {
    setLoading(true);
    setLoadingMessage('Loading sale return...');
    try {
      const res = await fetchData('SaleReturnApi', `/${sr.Id}`, token);
      const data = res.response_data || res;
      setEditReturnData(data);
      setEditModalOpen(true);
    } catch (e) {
      console.error(e);
      setError('Failed to load sale return for editing');
    }
    setLoading(false);
  };

  const numberToWords = (num) => {
    if (!num || num === 0) return 'Zero';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const cvt = (n) => {
      let r = '';
      if (n >= 100) { r += ones[Math.floor(n / 100)] + ' Hundred '; n %= 100; }
      if (n >= 20) { r += tens[Math.floor(n / 10)] + ' '; n %= 10; }
      else if (n >= 10) { r += teens[n - 10] + ' '; return r; }
      if (n > 0) r += ones[n] + ' ';
      return r;
    };
    let result = '';
    const cr = Math.floor(num / 10000000); if (cr > 0) { result += cvt(cr) + 'Crore '; num %= 10000000; }
    const lk = Math.floor(num / 100000); if (lk > 0) { result += cvt(lk) + 'Lakh '; num %= 100000; }
    const th = Math.floor(num / 1000); if (th > 0) { result += cvt(th) + 'Thousand '; num %= 1000; }
    if (num > 0) result += cvt(num);
    return result.trim();
  };

  const handlePrintReturn = async (srId) => {
    setLoading(true);
    setLoadingMessage('Generating PDF...');
    try {
      const res = await fetchData('SaleReturnApi', `/${srId}`, token);
      const data = res.response_data || res;
      const sr = data.SaleReturn || data;
      const details = data.SaleReturnDetails || [];

      // Fetch company / branch info
      let companyData = {}, branchData = {};
      try {
        const [coRes, brRes] = await Promise.allSettled([
          fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token),
          fetchData('BranchApi', `/BranchDetails/${branchId}`, token),
        ]);
        if (coRes.status === 'fulfilled') companyData = coRes.value?.response_data || coRes.value || {};
        if (brRes.status === 'fulfilled') branchData  = brRes.value?.response_data  || brRes.value  || {};
      } catch (e) { /* use defaults */ }

      // ── company / branch fields (same pattern as SaleIndex) ──────────────
      const coName       = companyData?.Name || 'Company Name';
      const coBranchName = branchData?.BranchName || branchData?.Name || '';
      const coGST        = branchData?.GSTNumber || companyData?.GSTNumber || '';
      const coState      = branchData?.StateName || companyData?.StateName || 'Kerala';
      const coStateCode  = branchData?.StateCode || companyData?.StateCode || '32';
      const coAddr       = [branchData?.AddressLine1, branchData?.AddressLine2, branchData?.Location, branchData?.PinCode]
                            .filter(Boolean).join(', ') || companyData?.Address || '';
      const coTel1       = branchData?.ContactNumber1 || companyData?.ContactNumber || '';
      const coTel2       = branchData?.ContactNumber2 || companyData?.ContactNumber2 || '';
      const coPhones     = [coTel1, coTel2].filter(Boolean).join(',');

      const black = [0, 0, 0];
      const white = [255, 255, 255];

      const pdf = new jsPDF('p', 'mm', 'a4');
      const PW = pdf.internal.pageSize.width;
      const PH = pdf.internal.pageSize.height;
      const M = 5;
      const CW = PW - 2 * M;

      const drawText = (text, x, y, opts = {}) => {
        const { fs = 8, fw = 'normal', col = black, align = 'left', mw = null } = opts;
        pdf.setFontSize(fs); pdf.setFont('helvetica', fw); pdf.setTextColor(...col);
        const str = String(text ?? '');
        if (mw) { const lines = pdf.splitTextToSize(str, mw); pdf.text(lines, x, y, { align }); return y + lines.length * fs * 0.38; }
        pdf.text(str, x, y, { align }); return y + fs * 0.38;
      };
      const hLine = (x1, y1, x2, y2, lw = 0.3) => {
        pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(lw); pdf.line(x1, y1, x2, y2);
      };
      const drawRect = (x, y, w, h, lw = 0.3) => {
        pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(lw); pdf.rect(x, y, w, h, 'S');
      };

      let y = M;

      // ── OUTER BORDER ──────────────────────────────────────────────────────
      drawRect(M, M, CW, PH - 2 * M, 0.5);

      // ── HEADER ────────────────────────────────────────────────────────────
      const logoSize  = 14;
      const rightColX = M + CW * 0.68;
      const leftTextX = M + logoSize + 6;
      const leftMaxW  = rightColX - leftTextX - 4;
      const rightValX = rightColX + 22;
      const rightMaxW = M + CW - rightValX - 2;

      pdf.setFontSize(7);
      const addrLines    = pdf.splitTextToSize(coAddr, leftMaxW);
      const headerH      = Math.max(26, 14 + addrLines.length * 3.5 + 8);

      try { pdf.addImage(MampillyLogo, 'PNG', M + 3, y + 3, logoSize, logoSize); } catch (e) {}

      drawText(coName.toUpperCase(), leftTextX, y + 7, { fs: 12, fw: 'bold' });
      let headerTextY = y + 12;
      pdf.setFontSize(7); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...black);
      pdf.text(addrLines, leftTextX, headerTextY);
      headerTextY += addrLines.length * 3.5;
      drawText(`GST NO : ${coGST}  State : ${coState} - ${coStateCode}`, leftTextX, headerTextY, { fs: 7 });
      headerTextY += 4;
      if (coPhones) drawText(coPhones, leftTextX, headerTextY, { fs: 7 });

      const billNo = [sr.BillPrefix, sr.BillNumber, sr.FinYear].filter(Boolean).join('/');
      const billDate = formatDate(sr.BillDate);

      hLine(rightColX, y, rightColX, y + headerH);
      let ry = y + 5;
      drawText('Return No',   rightColX + 2, ry, { fs: 7, fw: 'bold' }); drawText(billNo,              rightValX, ry, { fs: 7, mw: rightMaxW }); ry += 5;
      drawText('Date',        rightColX + 2, ry, { fs: 7, fw: 'bold' }); drawText(billDate,             rightValX, ry, { fs: 7, mw: rightMaxW }); ry += 5;
      drawText('Against Sale',rightColX + 2, ry, { fs: 7, fw: 'bold' }); drawText(sr.SaleNo || '-',     rightValX, ry, { fs: 7, mw: rightMaxW }); ry += 5;
      drawText('Branch',      rightColX + 2, ry, { fs: 7, fw: 'bold' }); drawText(coBranchName || '-',  rightValX, ry, { fs: 6.5, mw: rightMaxW });

      y += headerH;
      hLine(M, y, M + CW, y);

      // ── CREDIT NOTE TITLE + CUSTOMER SECTION ────────────────────────────
      drawText('CREDIT NOTE', M + CW / 2, y + 5, { fs: 9, fw: 'bold', align: 'center' });
      drawText('(SALE RETURN)', M + CW / 2, y + 10, { fs: 8, fw: 'bold', align: 'center' });
      y += 14;
      hLine(M, y, M + CW, y);

      const custName    = sr.CustomerName || '-';
      const custGST     = sr.GSTNumber || '';
      const custPhone   = sr.CustomerContactNumber || sr.CustomerPhone || '';
      const custAddress = sr.CustomerAddress || sr.Address || '';
      const custState   = sr.StateName || coState;
      const custStateCode = sr.StateCode || coStateCode;

      const midX = M + CW * 0.5;
      const custRowH = 22;
      drawText('To',       M + 2, y + 4,  { fs: 7, fw: 'bold' });
      drawText(custName,   M + 2, y + 8,  { fs: 8, fw: 'bold', mw: midX - M - 5 });
      let custY = y + 13;
      if (custAddress) { drawText(custAddress, M + 2, custY, { fs: 7, col: [60,60,60], mw: midX - M - 5 }); custY += 4; }
      if (custPhone)   { drawText(custPhone,   M + 2, custY, { fs: 7, col: [30,64,175], mw: midX - M - 5 }); }

      hLine(midX, y, midX, y + custRowH);
      drawText('GST No :',     midX + 2, y + 4,  { fs: 7, fw: 'bold' }); drawText(custGST,         midX + 22, y + 4,  { fs: 7 });
      drawText('Invoice Type:', midX + 2, y + 10, { fs: 7, fw: 'bold' }); drawText(sr.InvoiceTypeName || '-', midX + 22, y + 10, { fs: 7 });

      y += custRowH;
      hLine(M, y, M + CW, y);

      // State row
      drawText(`State : ${custState}-${custStateCode}`, M + 2, y + 4, { fs: 7 });
      if (sr.Remarks) drawText(`Remarks : ${sr.Remarks}`, M + CW * 0.35, y + 4, { fs: 7, mw: CW * 0.6 });
      y += 6;
      hLine(M, y, M + CW, y);

      // ── ITEMS TABLE ──────────────────────────────────────────────────────
      const hasIGST = details.some(d => (d.IGSTAmount || 0) > 0) || (sr.IGSTAmount || 0) > 0;
      let tableHead, tableBody;
      if (hasIGST) {
        tableHead = [['No', 'Item', 'HSN/SAC Code', 'Rate', 'Dis %', 'S Rate', 'Ret Qty', 'IGST\n%', 'IGST\nAmt', 'Total']];
        tableBody = details.map((d, i) => {
          const qty = Math.max(Number(d.Quantity || 1), 1);
          const sRate = Number(d.TaxableAmount || 0) / qty;
          return [i+1, d.ItemName||'', d.HSNCode||'', Number(d.Rate||0).toFixed(2), Number(d.DiscountPercentage||0) > 0 ? Number(d.DiscountPercentage).toFixed(2) : '', sRate.toFixed(2), Number(d.Quantity||0).toFixed(2), d.GSTRate||'0', Number(d.IGSTAmount||0).toFixed(2), Number(d.TotalAmount||0).toFixed(2)];
        });
      } else {
        tableHead = [[
          { content: 'No', rowSpan: 2 }, { content: 'Item', rowSpan: 2 }, { content: 'HSN/SAC Code', rowSpan: 2 },
          { content: 'Rate', rowSpan: 2 }, { content: 'Dis %', rowSpan: 2 }, { content: 'S Rate', rowSpan: 2 },
          { content: 'Ret Qty', rowSpan: 2 }, { content: 'CGST', colSpan: 2 }, { content: 'SGST', colSpan: 2 },
          { content: 'Total', rowSpan: 2 },
        ], ['%', 'Amt', '%', 'Amt']];
        tableBody = details.map((d, i) => {
          const qty = Math.max(Number(d.Quantity || 1), 1);
          const sRate = Number(d.TaxableAmount || 0) / qty;
          const gstRate = Number(d.GSTRate || 0);
          const halfRate = gstRate / 2;
          return [i+1, d.ItemName||'', d.HSNCode||'', Number(d.Rate||0).toFixed(2), Number(d.DiscountPercentage||0) > 0 ? Number(d.DiscountPercentage).toFixed(2) : '', sRate.toFixed(2), Number(d.Quantity||0).toFixed(2), halfRate > 0 ? halfRate.toFixed(1) : '0', Number(d.CGSTAmount||0).toFixed(2), halfRate > 0 ? halfRate.toFixed(1) : '0', Number(d.SGSTAmount||0).toFixed(2), Number(d.TotalAmount||0).toFixed(2)];
        });
      }
      autoTable(pdf, {
        startY: y, head: tableHead, body: tableBody, theme: 'grid',
        headStyles: { fillColor: white, textColor: black, fontSize: 7, fontStyle: 'bold', halign: 'center', cellPadding: 1.5, lineColor: black, lineWidth: 0.3 },
        bodyStyles: { fontSize: 7, textColor: black, cellPadding: 1.5, lineColor: black, lineWidth: 0.2 },
        columnStyles: hasIGST ? {
          0: { halign: 'center', cellWidth: 8 }, 1: { halign: 'left' }, 2: { halign: 'center', cellWidth: 22 },
          3: { halign: 'right', cellWidth: 18 }, 4: { halign: 'center', cellWidth: 12 }, 5: { halign: 'right', cellWidth: 20 },
          6: { halign: 'right', cellWidth: 12 }, 7: { halign: 'center', cellWidth: 10 }, 8: { halign: 'right', cellWidth: 18 },
          9: { halign: 'right', cellWidth: 22 },
        } : {
          0: { halign: 'center', cellWidth: 8 }, 1: { halign: 'left' }, 2: { halign: 'center', cellWidth: 22 },
          3: { halign: 'right', cellWidth: 17 }, 4: { halign: 'center', cellWidth: 12 }, 5: { halign: 'right', cellWidth: 18 },
          6: { halign: 'right', cellWidth: 12 }, 7: { halign: 'center', cellWidth: 10 }, 8: { halign: 'right', cellWidth: 16 },
          9: { halign: 'center', cellWidth: 10 }, 10: { halign: 'right', cellWidth: 16 }, 11: { halign: 'right', cellWidth: 20 },
        },
        margin: { left: M, right: M },
        tableLineColor: black, tableLineWidth: 0.3,
      });
      y = (pdf.lastAutoTable?.finalY || y + 30);

      // ── TOTALS ROW ───────────────────────────────────────────────────────
      const totalQty  = details.reduce((s, d) => s + Number(d.Quantity || 0), 0);
      const totalCGST = Number(sr.CGSTAmount || 0);
      const totalSGST = Number(sr.SGSTAmount || 0);
      const totalIGST = Number(sr.IGSTAmount || 0);
      const grandTotal   = Number(sr.TotalAmount || 0);
      const taxableAmount = Number(sr.Amount || sr.TaxableAmount || 0);

      const totalsRowH = 7;
      hLine(M, y, M + CW, y, 0.3);
      hLine(M, y + totalsRowH, M + CW, y + totalsRowH, 0.3);

      if (hasIGST) {
        const fw = [8,0,22,18,12,20,12,10,18,22]; fw[1] = CW - fw.reduce((s,w)=>s+w,0);
        let cx = M; const cp = fw.map(w=>{ const x=cx; cx+=w; return {x,width:w}; });
        drawText(totalQty.toFixed(0), cp[6].x+cp[6].width/2, y+5, { fs:7, fw:'bold', align:'center' });
        drawText(totalIGST.toFixed(2), cp[8].x+cp[8].width-1, y+5, { fs:7, fw:'bold', align:'right' });
        drawText(grandTotal.toFixed(2), cp[9].x+cp[9].width-1, y+5, { fs:7, fw:'bold', align:'right' });
      } else {
        const fw = [8,0,22,17,12,18,12,10,16,10,16,20]; fw[1] = CW - fw.reduce((s,w)=>s+w,0);
        let cx = M; const cp = fw.map(w=>{ const x=cx; cx+=w; return {x,width:w}; });
        drawText(totalQty.toFixed(0), cp[6].x+cp[6].width/2, y+5, { fs:7, fw:'bold', align:'center' });
        drawText(totalCGST.toFixed(2), cp[8].x+cp[8].width-1, y+5, { fs:7, fw:'bold', align:'right' });
        drawText(totalSGST.toFixed(2), cp[10].x+cp[10].width-1, y+5, { fs:7, fw:'bold', align:'right' });
        drawText(grandTotal.toFixed(2), cp[11].x+cp[11].width-1, y+5, { fs:7, fw:'bold', align:'right' });
      }
      y += totalsRowH;

      // ── BOTTOM: Summary (right side only, no bank) ──────────────────────
      const bottomY      = y;
      const summaryLabelX = M + CW * 0.55 + 3;
      const valX2         = M + CW - 3;

      let sy = bottomY + 5;
      const sumLine = (lbl, val) => {
        drawText(lbl, summaryLabelX, sy, { fs: 7, fw: 'bold' });
        drawText(val, valX2, sy, { fs: 7, align: 'right' });
        sy += 5;
      };
      sumLine('Taxable Amount', taxableAmount.toFixed(2));
      if (hasIGST) { sumLine('IGST', totalIGST.toFixed(2)); }
      else { sumLine('CGST', totalCGST.toFixed(2)); sumLine('SGST', totalSGST.toFixed(2)); }
      if (Number(sr.DiscountAmount||0) > 0) sumLine('Discount', Number(sr.DiscountAmount).toFixed(2));
      if (Number(sr.FreightAmount||0)  > 0) sumLine('Freight',  Number(sr.FreightAmount).toFixed(2));
      if (Number(sr.RoundOff||0)      !== 0) sumLine('Round Off', Number(sr.RoundOff).toFixed(2));

      const summaryDivX = M + CW * 0.55;
      hLine(summaryDivX, sy, M + CW, sy); sy += 1;
      drawText('Grand Total', summaryLabelX, sy + 4, { fs: 9, fw: 'bold' });
      drawText(grandTotal.toFixed(2), valX2, sy + 4, { fs: 9, fw: 'bold', align: 'right' });

      const bottomSectionEndY = sy + 8;
      hLine(summaryDivX, bottomY, summaryDivX, bottomSectionEndY);
      hLine(M, bottomSectionEndY, M + CW, bottomSectionEndY);

      // ── AMOUNT IN WORDS ──────────────────────────────────────────────────
      const wordsY = bottomSectionEndY + 5;
      drawText(`Rupees ${numberToWords(Math.floor(grandTotal))} and ${Math.round((grandTotal % 1) * 100)} Paise Only`,
        M + 3, wordsY, { fs: 7, fw: 'bold', mw: summaryDivX - M - 5 });

      // ── FOOTER ───────────────────────────────────────────────────────────
      const footerLineY = wordsY + 6;
      hLine(M, footerLineY, M + CW, footerLineY);
      const sigLineY = footerLineY + 22;
      hLine(summaryDivX, footerLineY, summaryDivX, sigLineY);
      if (sr.Remarks) drawText(sr.Remarks, M + 3, footerLineY + 5, { fs: 7, mw: summaryDivX - M - 8 });
      drawText(`For  ${coName.toUpperCase()}`, summaryDivX + 3, footerLineY + 5,  { fs: 8, fw: 'bold' });
      drawText('Authorised Signatory',         summaryDivX + 3, footerLineY + 18, { fs: 8, fw: 'bold' });
      hLine(M, sigLineY, M + CW, sigLineY, 0.5);

      pdf.autoPrint();
      const blob = pdf.output('blob');
      const url  = URL.createObjectURL(blob);
      const win  = window.open(url, '_blank');
      if (win) { win.onload = () => { win.print(); setTimeout(() => URL.revokeObjectURL(url), 1000); }; }
      else { const a = document.createElement('a'); a.href = url; a.download = `SaleReturn_${billNo}.pdf`; a.click(); URL.revokeObjectURL(url); }
      setSuccess(`Sale Return ${billNo} ready for printing.`);
    } catch (err) {
      console.error(err);
      setError('Failed to generate print.');
    }
    setLoading(false);
  };

  const handleDelete = (sr) => {
    setDeleteItemId(sr.Id);
    setDeleteItemInfo({ billNumber: sr.BillNumber, customer: sr.CustomerName || sr.MasterName });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteItemId) return;
    setDeleteConfirmOpen(false);
    setLoading(true);
    setLoadingMessage('Deleting sale return...');
    try {
      await deleteData('SaleReturnApi', deleteItemId, token);
      setSuccess('Sale return deleted successfully');
    logAudit(AUDIT_ACTION.DELETE, 'SaleReturnApi', deleteItemId, { Id: deleteItemId }, null);
      fetchSaleReturns();
    } catch (e) { console.error(e); setError('Failed to delete sale return'); }
    setLoading(false);
    setDeleteItemId(null);
  };

  const handleActivate = (sr) => {
    setActivateItemId(sr.Id);
    setActivateItemInfo({ billNumber: sr.BillNumber, customer: sr.CustomerName || sr.MasterName });
    setActivateConfirmOpen(true);
  };

  const handleConfirmActivate = async () => {
    if (!activateItemId) return;
    setActivateConfirmOpen(false);
    setLoading(true);
    setLoadingMessage('Activating sale return...');
    try {
      await activateData('SaleReturnApi', activateItemId, token);
      setSuccess('Sale return activated successfully');
      fetchSaleReturns();
    } catch (e) { console.error(e); setError('Failed to activate sale return'); }
    setLoading(false);
    setActivateItemId(null);
  };

  const formatCurrency = (n) => `₹${(parseFloat(n) || 0).toFixed(2)}`;
  const formatDate = (d) => { if (!d) return '-'; const dt = new Date(d); const day = String(dt.getDate()).padStart(2,'0'); const month = String(dt.getMonth()+1).padStart(2,'0'); return `${day}/${month}/${dt.getFullYear()}`; };

  const filtered = saleReturns.filter(r => {
    if (searchTerm && !r.BillNumber?.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !(r.CustomerName || r.MasterName || '').toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (fromDate && new Date(r.BillDate) < new Date(fromDate)) return false;
    if (toDate && new Date(r.BillDate) > new Date(toDate)) return false;
    if (filterStatus !== 'all') {
      if (filterStatus === 'paid' && (r.BalanceAmount > 0)) return false;
      if (filterStatus === 'unpaid' && (r.PaidAmount > 0)) return false;
      if (filterStatus === 'partial' && (r.PaidAmount === 0 || r.BalanceAmount === 0)) return false;
    }
    if (filterActive !== 'all') {
      if (filterActive === 'active' && r.IsActive !== true) return false;
      if (filterActive === 'inactive' && r.IsActive !== false) return false;
    }
    return true;
  });

  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filtered.slice(startIndex, startIndex + rowsPerPage);

  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', position: 'relative', color: 'black', fontFamily: 'Times New Roman' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Roman', textTransform: 'uppercase', color: 'black' }}>
            Sale Return Management
          </h4>
        </div>

        {/* Filters + Create Button */}
        {isMobile ? (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', overflowX: 'auto', pb: 0.5, pt: 1, WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }, overflowY: 'visible' }}>
              <Tooltip title="Search" arrow>
                <IconButton onClick={() => setShowSearch(v => !v)} size="small" sx={{ border: `1px solid ${showSearch ? '#667eea' : '#d1d5db'}`, borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, bgcolor: showSearch ? '#eff6ff' : 'transparent' }}>
                  <Search sx={{ fontSize: '16px', color: showSearch ? '#667eea' : 'inherit' }} />
                </IconButton>
              </Tooltip>
              <input type="date" lang="en-GB" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <input type="date" lang="en-GB" value={toDate} onChange={e => setToDate(e.target.value)} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0 }}>
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partial</option>
              </select>
              <select value={filterActive} onChange={e => { setFilterActive(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0 }}>
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <Tooltip title="Refresh" arrow>
                <IconButton onClick={fetchSaleReturns} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0 }}>
                  <Refresh sx={{ fontSize: '16px' }} />
                </IconButton>
              </Tooltip>
              {hasCreatePermission && (
                <Tooltip title="Add Sale Return" arrow>
                  <IconButton onClick={handleOpenCreate} disabled={loading} size="small" sx={{ background: '#2C3E6E', color: 'white', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, '&:hover': { background: '#1a2d5a' } }}>
                    <Add sx={{ fontSize: '18px' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {showSearch && (
              <Box sx={{ mt: 0.75 }}>
                <TextField autoFocus fullWidth size="small" placeholder="Search by Bill No, Customer..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '18px' }} /></InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '32px' } }}
                />
              </Box>
            )}
          </Box>
        ) : (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search by Bill No, Customer..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '20px' }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: '180px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '29px' } }}
            />
            <TextField size="small" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '12px', height: '29px' }, width: '140px' }} />
            <TextField size="small" type="date" value={toDate} onChange={e => setToDate(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '12px', height: '29px' }, width: '140px' }} />
            <TextField select size="small" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setCurrentPage(1); }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '12px', height: '29px' }, width: '130px' }}>
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="unpaid">Unpaid</MenuItem>
              <MenuItem value="partial">Partial</MenuItem>
            </TextField>
            <TextField select size="small" value={filterActive} onChange={e => { setFilterActive(e.target.value); setCurrentPage(1); }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '12px', height: '29px' }, width: '100px' }}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
            <Tooltip title="Refresh" arrow>
              <Button variant="outlined" onClick={fetchSaleReturns} disabled={loading} sx={{ borderRadius: '6px', minHeight: '29px', minWidth: '29px', padding: '4px 8px', fontSize: '12px', textTransform: 'none' }}>
                <Refresh sx={{ fontSize: '16px' }} />
              </Button>
            </Tooltip>
            {hasCreatePermission && (
              <Tooltip title="Add Sale Return" arrow>
                <Button variant="contained" onClick={handleOpenCreate} disabled={loading}
                  sx={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap' }}>
                  <Add sx={{ fontSize: '16px' }} /><span style={{ marginLeft: '4px' }}>Add Sale Return</span>
                </Button>
              </Tooltip>
            )}
          </div>
        )}

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow style={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', height: '27px' }}>
                  {['Sl No.', 'Return No', 'Date', 'Sale No', 'Customer', 'Total Amount', 'Status', 'Actions'].map((col, i) => (
                    <TableCell key={col} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px', textAlign: i === 7 ? 'right' : 'left' }}>
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                      No sale returns found
                    </TableCell>
                  </TableRow>
                ) : paginatedData.map((sr, index) => (
                  <TableRow key={sr.Id} style={{ background: sr.IsActive === false ? '#fee2e2' : (index % 2 === 0 ? '#ffffff' : '#f8f9fa'), borderBottom: '2px solid #f0f0f0' }}>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{startIndex + index + 1}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#1e40af', fontWeight: 600 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Assignment sx={{ fontSize: 16, color: '#1e40af' }} />
                        {sr.BillPrefix}/{sr.BillNumber}/{sr.FinYear}
                      </div>
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{formatDate(sr.BillDate)}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{sr.SaleNo || sr.SaleBillNumber || '-'}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{sr.CustomerName || sr.MasterName}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                      <Chip label={formatCurrency(sr.TotalAmount)} size="small" sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 600, fontSize: '11px', height: '20px' }} />
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                      <Chip label={sr.IsActive ? 'Active' : 'Inactive'} size="small" color={sr.IsActive ? 'success' : 'default'} sx={{ fontSize: '0.65rem', height: '20px' }} />
                    </TableCell>
                    <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Tooltip title="View" arrow>
                          <Button size="small" onClick={() => handleView(sr)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>👁️</Button>
                        </Tooltip>
                        <Tooltip title="Print" arrow>
                          <Button size="small" onClick={() => handlePrintReturn(sr.Id)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>🖨️</Button>
                        </Tooltip>
                        {sr.IsActive && hasEditPermission && (
                          <Tooltip title="Edit" arrow>
                            <Button size="small" onClick={() => handleEdit(sr)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>✏️</Button>
                          </Tooltip>
                        )}
                        {sr.IsActive ? (
                          hasDeletePermission && (
                            <Tooltip title="Delete" arrow>
                              <Button size="small" onClick={() => handleDelete(sr)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>🗑️</Button>
                            </Tooltip>
                          )
                        ) : (
                          hasEditPermission && (
                            <Tooltip title="Activate" arrow>
                              <Button size="small" onClick={() => handleActivate(sr)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>✅</Button>
                            </Tooltip>
                          )
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage}
          itemsPerPage={rowsPerPage} onItemsPerPageChange={v => { setRowsPerPage(Number(v)); setCurrentPage(1); }}
          totalItems={filtered.length} />
      </div>

      <LogoLoader open={loading} message={loadingMessage} />

      <Snackbar open={!!error || !!success} autoHideDuration={6000} onClose={() => { setError(''); setSuccess(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={() => { setError(''); setSuccess(''); }} severity={error ? 'error' : 'success'}>{error || success}</Alert>
      </Snackbar>

      {/* Create Modal */}
      <SaleReturnModal open={createModalOpen} onClose={() => setCreateModalOpen(false)}
        companyId={companyId} branchId={branchId} token={token} customers={customers}
        setLoading={setLoading} setLoadingMessage={setLoadingMessage}
        setError={setError} setSuccess={setSuccess}
        fetchSaleReturns={fetchSaleReturns} userId={userId}
        initialBillData={initialBillData} />

      {/* Edit Modal */}
      <SaleReturnModal open={editModalOpen} onClose={() => { setEditModalOpen(false); setEditReturnData(null); }}
        companyId={companyId} branchId={branchId} token={token} customers={customers}
        setLoading={setLoading} setLoadingMessage={setLoadingMessage}
        setError={setError} setSuccess={setSuccess}
        fetchSaleReturns={fetchSaleReturns} userId={userId}
        mode="edit" editData={editReturnData} />

      {/* View Modal */}
      <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} maxWidth="xl" fullWidth
        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', maxHeight: '90vh' } }}>
        <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0', bgcolor: selectedReturn?.IsActive === false ? '#fee2e2' : '#fafbfc', p: '5px 10px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#0f172a', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>View Sale Return</Typography>
              {selectedReturn?.IsActive === false && (
                <Chip label="INACTIVE" size="small" sx={{ bgcolor: '#991b1b', color: 'white', fontWeight: 700, fontSize: '9px', height: '16px', letterSpacing: '0.5px' }} />
              )}
            </Box>
            <IconButton onClick={() => setViewModalOpen(false)} size="small" sx={{ bgcolor: selectedReturn?.IsActive === false ? '#fecaca' : '#f1f5f9', p: '2px', '&:hover': { bgcolor: selectedReturn?.IsActive === false ? '#fca5a5' : '#e2e8f0' } }}>
              <CloseIcon sx={{ fontSize: '15px' }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: '8px 10px', bgcolor: selectedReturn?.IsActive === false ? '#fef2f2' : '#fafbfc' }}>
          {selectedReturn && (
            <>
              {/* Header Info */}
              <Box sx={{ bgcolor: selectedReturn?.IsActive === false ? '#fee2e2' : '#dbeafe', borderRadius: 1, p: '5px 8px', mb: 1, border: selectedReturn?.IsActive === false ? '1px solid #fca5a5' : '1px solid #93c5fd' }}>
                <Grid container spacing={0}>
                  {[
                    ['Return No', [selectedReturn.BillPrefix, selectedReturn.BillNumber, selectedReturn.FinYear].filter(Boolean).join('/') || selectedReturn.BillNumber || '-'],
                    ['Return Date', formatDate(selectedReturn.BillDate)],
                    ['Sale No', selectedReturn.SaleNo || selectedReturn.SaleBillNumber || '-'],
                    ['Customer', selectedReturn.CustomerName || selectedReturn.MasterName],
                    ['GST No', selectedReturn.GSTNumber || '-'],
                    ['Invoice Type', selectedReturn.InvoiceTypeName || '-'],
                    ['Remarks', selectedReturn.Remarks || '-'],
                  ].map(([label, value]) => (
                    <Grid item xs={6} sm={3} md={2} key={label} sx={{ p: '3px 6px !important' }}>
                      <Typography sx={{ fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', display: 'block', textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.4px', lineHeight: 1.2 }}>{label}</Typography>
                      <Typography sx={{ fontWeight: 600, color: '#0f172a', fontSize: '11px', lineHeight: 1.4 }}>{value}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Items Table */}
              <Box sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: 700, color: '#0f172a', mb: '3px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Return Items</Typography>
                <Box sx={{ bgcolor: 'white', borderRadius: 1, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: selectedReturn?.IsActive === false ? '#fee2e2' : '#dbeafe', borderBottom: selectedReturn?.IsActive === false ? '1px solid #fca5a5' : '1px solid #93c5fd' }}>
                          {['#', 'ITEM NAME', 'HSN', 'QTY', 'RATE', 'AMOUNT', 'DISC%', 'DISC AMT', 'GST%', 'TAXABLE', 'CGST', 'SGST', 'IGST', 'TOTAL'].map(h => (
                            <th key={h} style={{ padding: '4px 6px', textAlign: ['QTY', 'RATE', 'AMOUNT', 'DISC%', 'DISC AMT', 'GST%', 'TAXABLE', 'CGST', 'SGST', 'IGST', 'TOTAL'].includes(h) ? 'right' : 'left', fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '9px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {returnDetails.map((item, idx) => (
                          <tr key={item.Id} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                            <td style={{ padding: '3px 6px', fontSize: '11px', fontWeight: 500 }}>{idx + 1}</td>
                            <td style={{ padding: '3px 6px', fontSize: '11px', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>{item.ItemName}</td>
                            <td style={{ padding: '3px 6px', fontSize: '11px', color: '#64748b' }}>{item.HSNCode || '-'}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '11px', fontWeight: 700, color: '#92400e' }}>{item.Quantity}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '11px', fontWeight: 600 }}>{formatCurrency(item.Rate)}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '11px', color: '#64748b' }}>{formatCurrency(item.Amount)}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '11px', color: '#64748b' }}>{item.DiscountPercentage || 0}%</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '11px', color: '#dc2626', fontWeight: 600 }}>{formatCurrency(item.DiscountAmount)}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '11px', fontWeight: 700, color: '#065f46' }}>{item.GSTRate || 0}%</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '11px', color: '#64748b' }}>{formatCurrency(item.TaxableAmount)}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '11px', color: '#dc2626', fontWeight: 600 }}>{formatCurrency(item.CGSTAmount)}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '11px', color: '#dc2626', fontWeight: 600 }}>{formatCurrency(item.SGSTAmount)}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '11px', color: '#dc2626', fontWeight: 600 }}>{formatCurrency(item.IGSTAmount)}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '11px', fontWeight: 700, color: '#0f172a' }}>{formatCurrency(item.TotalAmount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </Box>

              {/* Summary */}
              <Box sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: 700, color: '#0f172a', mb: '3px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Return Summary</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={8}>
                    <Box sx={{ bgcolor: 'white', borderRadius: 1, p: '5px 8px', border: '1px solid #e2e8f0' }}>
                      <Grid container spacing={0}>
                        {[
                          ['Subtotal', selectedReturn.Amount, '#0f172a', '#f8fafc'],
                          ['Discount', selectedReturn.DiscountAmount, '#dc2626', '#fff5f5'],
                          ['Taxable', selectedReturn.TaxableAmount, '#0f172a', '#f8fafc'],
                          ['CGST', selectedReturn.CGSTAmount, '#dc2626', '#fee2e2'],
                          ['SGST', selectedReturn.SGSTAmount, '#dc2626', '#fee2e2'],
                          ['IGST', selectedReturn.IGSTAmount, '#dc2626', '#fee2e2'],
                          ['Freight', selectedReturn.FreightAmount, '#0f172a', '#f8fafc'],
                          ['Freight GST', selectedReturn.FreightGSTAmount, '#dc2626', '#f8fafc'],
                          ['Packing', selectedReturn.PackingAmount, '#0f172a', '#f8fafc'],
                          ['Packing GST', selectedReturn.PackingGSTAmount, '#dc2626', '#f8fafc'],
                          ['TCS', selectedReturn.TCSAmount, '#0f172a', '#f8fafc'],
                          ['Round Off', selectedReturn.RoundOff, '#0f172a', '#f8fafc'],
                        ].map(([label, val, color, bg]) => (
                          <Grid item xs={6} sm={4} md={2} key={label} sx={{ p: '2px 4px !important' }}>
                            <Box sx={{ textAlign: 'center', p: '3px 4px', bgcolor: bg, borderRadius: '4px', border: '1px solid #f0f0f0' }}>
                              <Typography sx={{ color: '#64748b', fontSize: '9px', fontWeight: 600, lineHeight: 1.2 }}>{label}</Typography>
                              <Typography sx={{ fontWeight: 700, color, fontSize: '11px', lineHeight: 1.3 }}>{formatCurrency(val)}</Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ bgcolor: 'white', borderRadius: 1, p: '5px 8px', border: '1px solid #e2e8f0', height: '100%', display: 'flex', flexDirection: 'column', gap: '4px', justifyContent: 'center' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '4px 8px', bgcolor: '#fef3c7', borderRadius: '4px', border: '1px solid #fde68a' }}>
                        <Typography sx={{ color: '#92400e', fontSize: '10px', fontWeight: 700 }}>Return Total</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#ea580c', fontSize: '12px' }}>{formatCurrency(selectedReturn.TotalAmount)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '4px 8px', bgcolor: '#dcfce7', borderRadius: '4px', border: '1px solid #86efac' }}>
                        <Typography sx={{ color: '#166534', fontSize: '10px', fontWeight: 700 }}>Paid Amount</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#16a34a', fontSize: '12px' }}>{formatCurrency(selectedReturn.PaidAmount)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '4px 8px', bgcolor: '#dbeafe', borderRadius: '4px', border: '1px solid #93c5fd' }}>
                        <Typography sx={{ color: '#1e40af', fontSize: '10px', fontWeight: 700 }}>Balance Amount</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#2563eb', fontSize: '12px' }}>{formatCurrency(selectedReturn.BalanceAmount)}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </DialogContent>
        <Box sx={{ p: '5px 10px', borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => setViewModalOpen(false)} sx={{ textTransform: 'none', borderRadius: 1, px: 2, fontWeight: 600, fontSize: '11px', minHeight: '26px', py: '3px' }}>Close</Button>
        </Box>
      </Dialog>
    
      {/* Delete Confirm */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Warning color="error" /> Confirm Delete</DialogTitle>
        <DialogContent><Typography>Delete sale return <strong>{deleteItemInfo.billNumber}</strong> for <strong>{deleteItemInfo.customer}</strong>?</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Activate Confirm */}
      <Dialog open={activateConfirmOpen} onClose={() => setActivateConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CheckCircle color="success" /> Confirm Activate</DialogTitle>
        <DialogContent><Typography>Activate sale return <strong>{activateItemInfo.billNumber}</strong>?</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmActivate} color="success" variant="contained">Activate</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SaleReturnIndex;

